import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Carousel, Dropdown } from 'react-bootstrap';

import { UseOutletContextStorageCenter } from './StorageCenter';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ProductDA } from '../../../../services/admin/product.service';
import { StorageCenterDA } from '../../../../services/admin/storage.center.service';
import { Error } from '../../../../models/error.model';
import { Message } from '../../../../models/message.model';
import { Variant } from '../../../../models/variant.model';
import { CompanyProductVariants } from '../../../../models/company.product.variants.model';
import { StorageCenter } from '../../../../models/storage.center.model';
import { storageCenterSelected } from '../../../../scripts/list.selected.script';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError, getMessage } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { modalShow } from '../../../../utils/modal.util';
import { formatNumberAddress } from '../../../../scripts/format.value.script';
import { countryName } from '../../../../libraries/country.library';
import { paginationRow, sortColumn } from '../../../../scripts/table.script';
import { imageProduct } from '../../../../scripts/image.value.script';
import { suffixLegend } from '../../../../scripts/name.legend.script';

import AppSortTable from '../../../../components/element/SortTable';
import AppPreloader from '../../../../components/Preloader';
import AppModalStorageCenterUpdate from '../../../../components/modal/ModalStorageCenterUpdate';
import AppModalStorageOperation from '../../../../components/modal/ModalStorageOperation';
import AppPagination from '../../../../components/element/Pagination';
import imgProductDefault from '../../../../assets/themes/product.png';

export interface StorageCenterViewPageProps {};

let errorResponse: Error, messageResponse: Message, storageCenterResponse: StorageCenter, companyProductVariantsResponse: CompanyProductVariants;

const StorageCenterViewPage: React.FunctionComponent<StorageCenterViewPageProps> = props => {
  const {setRoute, companyForUser, storageCentersForCompany, loadStorageCentersForCompany} = UseOutletContextStorageCenter()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [filter, setFilter] = useState({storage_center: '', search: '', options: {sort_field: '', sort_mode: ''}})
  const [pagination, setPagination] = useState({current: 0, total: 0, pages: [] as number[], limits: {min: 0, max: 0}})
  const [variants, setVariants] = useState<Variant[] | null>(null)
  const [storageCenter, setStorageCenter] = useState<StorageCenter | null>(null)

  const loadStorageCenter = async (id_company: string, id_storage_center: string) => {
    await StorageCenterDA.getStorageCenter(id_company, id_storage_center).then( (response) => {
      if (response.status === 200) {
        storageCenterResponse = response.data?.data ?? response.data
        setStorageCenter(storageCenterResponse)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadProductVariants = async (id_company: string, id_storage_center: string) => {
    let pagesOfPagination: number[] = []
    let totalByPage: number = pagination.total || 10

    await ProductDA.getProductVariants(id_company, id_storage_center, filter.search, filter.options.sort_field, filter.options.sort_mode).then( (response) => {
      if (response.status === 200) {
        companyProductVariantsResponse = response.data?.data ?? response.data
        const variantItems = companyProductVariantsResponse.items ?? companyProductVariantsResponse.product_variants ?? []

        for (let i = 1; i <= Math.ceil(variantItems.length / totalByPage); i++) {
          pagesOfPagination.push(i)
        }

        setVariants(variantItems)
        setFilter({storage_center: companyProductVariantsResponse.filter?.storage_center ?? '', search: companyProductVariantsResponse.filter?.name ?? '', options: {sort_field: companyProductVariantsResponse.filter?.sort_field ?? '', sort_mode: companyProductVariantsResponse.filter?.sort_mode ?? ''}})
        setPagination({current: 1, total: totalByPage, pages: pagesOfPagination, limits: {min: 0, max: totalByPage}})
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const submitStorageCenterDelete = async () => {
    if (companyForUser && storageCenter) {
      GlobalSwal.fire({
        title: lang.labels.youWantToDeleteTheStorageCenter,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesDelete,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await StorageCenterDA.deleteStorageCenters(companyForUser.id, [storageCenter.id]).then( (response) => {
            if (response.status === 200) {
              messageResponse = response.data

              GlobalSwal.fire({
                title: getMessage(messageResponse.message, lang.code),
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
                }).then( () => {
                  loadStorageCentersForCompany(companyForUser.id)
                  navigate('/main/inventory/storage/list', {replace: true})
                })            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const handleChangeSearch = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    if (companyForUser && param.id) {
      filter.search = event.target.value
      filter.options.sort_field= ''
      filter.options.sort_mode = ''
      loadProductVariants(companyForUser.id, param.id)
    }
  }

  const executeStorageCenterUpdate = () => {
    modalShow('modal-storage-center-update')
  }

  const executeStorageCenter = (item: string) => {
    filter.storage_center = item
    filter.search = ''
    filter.options.sort_field= ''
    filter.options.sort_mode = ''
    navigate(`/main/inventory/storage/view/${item}`, {replace: true})
  }

  const executeSortList = (item: string) => {
    if (companyForUser && param.id) {
      sortColumn(item, filter.options)
      loadProductVariants(companyForUser.id, param.id)
    }
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadStorageCenter(companyForUser.id, param.id)
      loadProductVariants(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.inventory, branch: lang.labels.viewStorageCenter}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && storageCentersForCompany && storageCenter && variants
        ?
        <>
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <div className="symbol symbol-square me-5">
                  <div className="symbol-label bg-transparent border border-secondary border-dashed w-40px h-40px">
                    <span className="svg-icon svg-icon-2x svg-icon-primary">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path opacity="0.3" d="M18.0624 15.3454L13.1624 20.7453C12.5624 21.4453 11.5624 21.4453 10.9624 20.7453L6.06242 15.3454C4.56242 13.6454 3.76242 11.4452 4.06242 8.94525C4.56242 5.34525 7.46242 2.44534 11.0624 2.04534C15.8624 1.54534 19.9624 5.24525 19.9624 9.94525C20.0624 12.0452 19.2624 13.9454 18.0624 15.3454ZM13.0624 10.0453C13.0624 9.44534 12.6624 9.04534 12.0624 9.04534C11.4624 9.04534 11.0624 9.44534 11.0624 10.0453V13.0453H13.0624V10.0453Z" fill="black"/>
                        <path d="M12.6624 5.54531C12.2624 5.24531 11.7624 5.24531 11.4624 5.54531L8.06241 8.04531V12.0453C8.06241 12.6453 8.46241 13.0453 9.06241 13.0453H11.0624V10.0453C11.0624 9.44531 11.4624 9.04531 12.0624 9.04531C12.6624 9.04531 13.0624 9.44531 13.0624 10.0453V13.0453H15.0624C15.6624 13.0453 16.0624 12.6453 16.0624 12.0453V8.04531L12.6624 5.54531Z" fill="black"/>
                      </svg>
                    </span>
                  </div>
                </div>
                <h3>{storageCenter.name}</h3>
              </div>
              <div className="card-toolbar">
                <div className="d-flex justify-content-end align-items-center">
                  { storageCentersForCompany.length > 1 &&
                    <Dropdown>
                      <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-200px p-0 me-2">
                        <span className="selection">
                          <span className="select2-selection select2-selection--single form-select">
                            <span className="select2-selection__rendered" role="textbox">
                              {storageCenterSelected(storageCentersForCompany, filter.storage_center)}
                            </span>
                          </span>
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100" align="end">
                        <span className="select2-dropdown select2-dropdown--below">
                          <span className="select2-results">
                            <ul className="select2-results__options" role="listbox">
                              { storageCentersForCompany.map (( (item, index) => { return (
                                <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === filter.storage_center && "select2-results__option--selected"}`} role="option" aria-selected={item.id === filter.storage_center}>
                                  <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => executeStorageCenter(item.id)}>{item.name}</Dropdown.Item>
                                </li>
                              )}))}
                            </ul>
                          </span>
                        </span>
                      </Dropdown.Menu>
                    </Dropdown>
                  }
                  <button className="btn btn-light-success me-2" type="button" onClick={() => modalShow('modal-storage-operation-in')}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12C22 17.5 17.5 22 12 22ZM11 8V16H13V8H11ZM8 11V13H16V11H8Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.stockEntry}
                    </div>
                  </button>
                  <button className="btn btn-light-danger me-2" type="button" onClick={() => modalShow('modal-storage-operation-out')}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12C22 17.5 17.5 22 12 22ZM8 11V13H16V11H8Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.stockExit}
                    </div>
                  </button>
                  <Link to={"/main/inventory/storage/operation/" + storageCenter.id} className="btn btn-light-primary me-2">
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 7H3C2.4 7 2 6.6 2 6V4C2 3.4 2.4 3 3 3H21C21.6 3 22 3.4 22 4V6C22 6.6 21.6 7 21 7Z" fill="black"/>
                          <path opacity="0.3" d="M21 14H3C2.4 14 2 13.6 2 13V11C2 10.4 2.4 10 3 10H21C21.6 10 22 10.4 22 11V13C22 13.6 21.6 14 21 14ZM22 20V18C22 17.4 21.6 17 21 17H3C2.4 17 2 17.4 2 18V20C2 20.6 2.4 21 3 21H21C21.6 21 22 20.6 22 20Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.operations}
                    </div>
                  </Link>
                  <button className="btn btn-primary me-2" type="button" onClick={executeStorageCenterUpdate}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" fillRule="evenodd" clipRule="evenodd" d="M2 4.63158C2 3.1782 3.1782 2 4.63158 2H13.47C14.0155 2 14.278 2.66919 13.8778 3.04006L12.4556 4.35821C11.9009 4.87228 11.1726 5.15789 10.4163 5.15789H7.1579C6.05333 5.15789 5.15789 6.05333 5.15789 7.1579V16.8421C5.15789 17.9467 6.05333 18.8421 7.1579 18.8421H16.8421C17.9467 18.8421 18.8421 17.9467 18.8421 16.8421V13.7518C18.8421 12.927 19.1817 12.1387 19.7809 11.572L20.9878 10.4308C21.3703 10.0691 22 10.3403 22 10.8668V19.3684C22 20.8218 20.8218 22 19.3684 22H4.63158C3.1782 22 2 20.8218 2 19.3684V4.63158Z" fill="black"/>
                          <path d="M10.9256 11.1882C10.5351 10.7977 10.5351 10.1645 10.9256 9.77397L18.0669 2.6327C18.8479 1.85165 20.1143 1.85165 20.8953 2.6327L21.3665 3.10391C22.1476 3.88496 22.1476 5.15129 21.3665 5.93234L14.2252 13.0736C13.8347 13.4641 13.2016 13.4641 12.811 13.0736L10.9256 11.1882Z" fill="black"/>
                          <path d="M8.82343 12.0064L8.08852 14.3348C7.8655 15.0414 8.46151 15.7366 9.19388 15.6242L11.8974 15.2092C12.4642 15.1222 12.6916 14.4278 12.2861 14.0223L9.98595 11.7221C9.61452 11.3507 8.98154 11.5055 8.82343 12.0064Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.edit}
                    </div>
                  </button>
                  <button className="btn btn-primary" type="button" onClick={submitStorageCenterDelete}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black"/>
                          <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black"/>
                          <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.delete}
                    </div>
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="d-flex flex-wrap">
                <div className="flex-equal me-5">
                  <table className="table align-middle gy-0 m-0">
                    <tbody className="fw-bold text-secondary">
                      <tr>
                        <td className="w-200px text-black">{lang.labels.street}</td>
                        <td className="min-w-200px fs-6">{storageCenter.address.street}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-black">{lang.labels.number}</td>
                        <td className="min-w-200px fs-6">{formatNumberAddress(lang, storageCenter.address.number)}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-black">{lang.labels.additional}</td>
                        <td className="min-w-200px fs-6">{(storageCenter.address?.additional ?? '').length > 0 ? storageCenter.address.additional : lang.labels.none}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="flex-equal">
                  <table className="table align-middle gy-0 m-0">
                    <tbody className="fw-bold text-secondary">
                      <tr>
                        <td className="w-200px text-black">{lang.labels.administrativeArea}</td>
                        <td className="min-w-200px fs-6">{storageCenter.address.administrative_area}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-black">{lang.labels.city}</td>
                        <td className="min-w-200px fs-6">{storageCenter.address.city}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-black">{lang.labels.zipCode}</td>
                        <td className="min-w-200px fs-6">{(storageCenter.address?.zip_code ?? '').length > 0 ? storageCenter.address.zip_code : lang.labels.none}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-black">{lang.labels.country}</td>
                        <td className="min-w-200px fs-6">{countryName(lang.code, storageCenter.address.country)}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-black">{lang.labels.afterSalesService}</td>
                        <td className="min-w-200px fs-6">
                          <span className={`badge badge-light-${storageCenter.is_sav ? 'success' : 'danger'}`}>{storageCenter.is_sav ? lang.labels.yes : lang.labels.no}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="card card-flush">
            <div className="card-header">
              <div className="card-title">
                <div className="d-flex align-items-center position-relative">
                  <span className="svg-icon svg-icon-1 position-absolute ms-4">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="black" />
                      <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="black" />
                    </svg>
                  </span>
                  <input className="form-control w-350px ps-14" type="text" autoComplete="off" placeholder={lang.labels.searchProduct} value={filter.search} onChange={handleChangeSearch} />
                </div>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="dataTables_wrapper">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed mb-2">
                    <thead className="border-bottom">
                      <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                        <th className="w-75px">{lang.labels.image}</th>
                        <th className="min-w-200px">
                          <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("product")}>
                            <span className="me-1">{lang.labels.product}</span>
                            <AppSortTable attribute={"product"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="min-w-200px">
                          <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("description")}>
                            <span className="me-1">{lang.labels.description}</span>
                            <AppSortTable attribute={"description"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                          </div>
                        </th>
                        <th className="w-125px">
                          <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("quantity")}>
                            <span className="me-1">{lang.labels.quantity}</span>
                            <AppSortTable attribute={"quantity"} field={filter.options.sort_field} mode={filter.options.sort_mode}></AppSortTable>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    { variants.length > 0
                      ?
                      <tbody className="text-secondary fw-bold">
                        { variants.map (( (item, index) => { return (
                          <tr key={index} className={`row-dashed ${paginationRow(index, pagination.limits)}`}>
                            <td className="d-flex align-items-center">
                              { (item.photos ?? []).length > 0
                                ?
                                <Carousel slide={false} controls={false} indicators={false} touch={false}>
                                  { item.photos.map (( (item, index) => { return (
                                    <Carousel.Item key={index}>
                                      <img src={imageProduct(item)} className="d-block w-35px h-35px" alt={`${lang.labels.photography}${suffixLegend(index)}`} />
                                    </Carousel.Item>
                                  )}))}
                                </Carousel>
                                :
                                <div className="symbol symbol-35px overflow-hidden">
                                  <span className="symbol-label">
                                    <img src={imgProductDefault} alt={lang.labels.photography} className="w-100 h-100" />
                                  </span>
                                </div>
                              }
                            </td>
                            <td className="text-dark">{item.display_name}: {item.description_short}</td>
                            <td>{(item.description_long ?? '').length > 0 ? item.description_long : lang.labels.none}</td>
                            <td className={`text-center fw-bolder ${item.stock > 0 ? "text-success" : "text-danger"}`}>{item.stock}</td>
                          </tr>
                        )}))}
                      </tbody>
                      :
                      <tbody>
                        <tr className="text-secondary fw-bold">
                          <td colSpan={4} className="text-center">{lang.labels.noRecordsFound}</td>
                        </tr>
                      </tbody>
                    }
                  </table>
                </div>
                {/* <AppPagination property={{total_items: variants.length, show_options: true}} pagination={pagination} setPagination={setPagination}></AppPagination> */}
              </div>
            </div>
          </div>
        </>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalStorageCenterUpdate companyForUser={companyForUser} storageCentersForCompany={storageCentersForCompany} storageCenter={storageCenter} setStorageCenter={setStorageCenter} loadStorageCentersForCompany={loadStorageCentersForCompany}></AppModalStorageCenterUpdate>
      <AppModalStorageOperation companyForUser={companyForUser} operationType="in" preselectedStorageCenter={param.id} storageCentersForCompany={storageCentersForCompany} onSuccess={() => { if (companyForUser && param.id) { loadStorageCenter(companyForUser.id, param.id); loadProductVariants(companyForUser.id, param.id) } }}></AppModalStorageOperation>
      <AppModalStorageOperation companyForUser={companyForUser} operationType="out" preselectedStorageCenter={param.id} storageCentersForCompany={storageCentersForCompany} onSuccess={() => { if (companyForUser && param.id) { loadStorageCenter(companyForUser.id, param.id); loadProductVariants(companyForUser.id, param.id) } }}></AppModalStorageOperation>
    </div>
  )
};

export default StorageCenterViewPage;
